import Constants from "expo-constants";
import { Platform } from "react-native";

export type DeploymentEnvironment = "local" | "preview" | "production";

function resolveDeploymentEnvironment(): DeploymentEnvironment {
  if (Platform.OS === "web") {
    // The web build runs directly on its real hosting domains -- read the
    // environment off the actual browser location instead of threading a
    // build-time env var through Metro. (Constants.expoConfig isn't
    // reliably populated at runtime in a static web export, and Metro's
    // transform cache doesn't reliably bust on env var changes either --
    // both bit us before landing on this.) `window` is undefined during
    // the static-export prerender step (Node.js, not a browser); "local"
    // there is harmless since hydration re-evaluates this in the real
    // browser immediately after.
    if (typeof window === "undefined") return "local";
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return "local";
    // Cloudflare Workers: use.<domain> is production, dev-use.<domain> (and
    // any *.workers.dev URL) is preview.
    if (host.startsWith("dev-use.") || host.endsWith(".workers.dev"))
      return "preview";
    return "production";
  }
  // Native builds: `ENVIRONMENT` in .env (or the eas.json build profile) reaches
  // the app through app.config.ts. DEVELOPMENT is the local DDEV stack, TESTING
  // the Coolify preview API; anything else, unset included, is production.
  const requested = String(
    Constants.expoConfig?.extra?.environment ?? "",
  ).toUpperCase();
  return requested === "DEVELOPMENT"
    ? "local"
    : requested === "TESTING"
      ? "preview"
      : "production";
}

const deploymentEnvironment = resolveDeploymentEnvironment();

type EnvironmentConstants = {
  siteUrl: string;
  apiUrl: string;
  sentryAppDsn?: string;
};

// Sentry DSN is a public ingest endpoint (safe to commit). `local` omits it so
// dev errors don't reach the shared project.
const sentryAppDsn =
  "https://4e44f5d8714b5df340ae7aef983516e0@o4504087623761920.ingest.us.sentry.io/4511953350295552";

// Local development talks to the DDEV site (`ddev start` in doughbro-api).
const constantsByEnvironment: Record<
  DeploymentEnvironment,
  EnvironmentConstants
> = {
  local: {
    siteUrl: "http://localhost:3000",
    apiUrl: "https://doughbro-api.ddev.site",
  },
  preview: {
    siteUrl: "https://doughbro-website.pages.dev",
    apiUrl: "https://dev-api.doughbro.app",
    sentryAppDsn,
  },
  production: {
    siteUrl: "https://www.doughbro.app",
    apiUrl: "https://api.doughbro.app",
    sentryAppDsn,
  },
};

const environmentConstants = constantsByEnvironment[deploymentEnvironment];

export const DEPLOYMENT_ENVIRONMENT = deploymentEnvironment;

// Cloudflare Turnstile site key (public). Off for the local stack so local
// sign-in and the e2e suite don't need to reach Cloudflare; the secret is the
// API's TURNSTILE_SECRET_KEY, never here.
export const TURNSTILE_SITE_KEY =
  deploymentEnvironment === "local" ? "" : "0x4AAAAAAEsjEkGY-JqAYG_f";
export const SITE_URL = environmentConstants.siteUrl;
export const SENTRY_APP_DSN = environmentConstants.sentryAppDsn;
// Override with EXPO_PUBLIC_API_URL, e.g. for a phone on the LAN.
export const API_URL =
  process.env.EXPO_PUBLIC_API_URL?.trim() || environmentConstants.apiUrl;

export const SENTRY_ENABLED = Boolean(SENTRY_APP_DSN);
export const backendConfigured = Boolean(API_URL);

// RevenueCat SDK keys are public identifiers, never server secrets.
// RevenueCat Web Billing (Stripe): live keys on production, sandbox keys (Stripe
// test cards) everywhere else.
const REVENUECAT_WEB_KEYS = {
  sandbox: "rcb_sb_HNXLVwMQiIMEHTMALTldozhxk",
  production: "rcb_bTZgQzFKbfULWnRcSupqZQWZhrYm",
} as const;

export const REVENUECAT_API_KEYS = {
  ios: "appl_RMsAYpjijNTsPoGCTjMAuIPRHIC",
  android: "goog_sZoSOyiqheruzXkwWHMfbcdnOdb",
  // Test Store key: non-production native builds.
  test: "test_qnksofbNMWnmccADXPkfEWAPavj",
  web:
    DEPLOYMENT_ENVIRONMENT === "production"
      ? REVENUECAT_WEB_KEYS.production
      : REVENUECAT_WEB_KEYS.sandbox,
} as const;

/** Retained for code that reads config as one object. */
export const publicConfig = {
  apiUrl: API_URL,
  websiteUrl: SITE_URL,
  deploymentEnvironment: DEPLOYMENT_ENVIRONMENT,
};

export const STORE_LINKS = {
  ios: "",
  android: "",
} as const;
